import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { applyPdfAxisFontBoost, nextFrame } from "./plotlyPdfAxisFontBoost";
import { pdfTextCenteredWithWr } from "../../../utils/samplingDisplayLabels";

export const PONDSTAT_EXPORT_FOOTER = "Calculated and Produced by PondStat";
export const PONDSTAT_EXPORT_FOOTER_FONT_SIZE = 8;
export const PONDSTAT_EXPORT_FOOTER_COLOR_RGB = [100, 100, 100];

/** Bottom border footer for exported PDF pages (light gray, centered). */
export function drawPondStatPdfFooter(pdf, pageWidth, pageHeight, margin = 15) {
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(PONDSTAT_EXPORT_FOOTER_FONT_SIZE);
  pdf.setTextColor(...PONDSTAT_EXPORT_FOOTER_COLOR_RGB);
  pdf.setDrawColor(200, 200, 200);
  pdf.line(margin, pageHeight - 14, pageWidth - margin, pageHeight - 14);
  const textWidth =
    (pdf.getStringUnitWidth(PONDSTAT_EXPORT_FOOTER) * pdf.internal.getFontSize()) /
    pdf.internal.scaleFactor;
  pdf.text(PONDSTAT_EXPORT_FOOTER, (pageWidth - textWidth) / 2, pageHeight - 8);
}

/** Style the PondStat footer row in an Excel worksheet (0-based row index). */
export function applyExcelPondStatFooterStyle(sheet, footerRowIndex) {
  if (footerRowIndex == null || footerRowIndex < 0) return;
  const cellKey = `A${footerRowIndex + 1}`;
  const cell = sheet[cellKey];
  if (!cell) return;
  cell.s = {
    font: {
      sz: PONDSTAT_EXPORT_FOOTER_FONT_SIZE,
      color: { rgb: "646464" },
    },
    alignment: { horizontal: "center", vertical: "center" },
  };
}

const HTML2CANVAS_OPTS = {
  scale: 2,
  useCORS: true,
  logging: false,
  backgroundColor: "#ffffff",
  ignoreElements: (el) =>
    Boolean(
      el?.dataset?.pdfExclude === "true" ||
        el?.classList?.contains?.("sampling-pdf-exclude")
    ),
};

/**
 * Capture the graph as rendered in the DOM (html2canvas). Matches on-screen output.
 */
async function captureGraphRaster(graphRef, plotExportRef) {
  const container = graphRef.current;
  if (!container) {
    throw new Error("No graph container");
  }

  const plotEl = plotExportRef?.current?.getPlotElement?.() ?? null;
  const captureTarget =
    plotEl && container.contains(plotEl) ? plotEl : container;

  const restore = await applyPdfAxisFontBoost(plotEl);
  await nextFrame();

  try {
    let canvas;
    try {
      canvas = await html2canvas(captureTarget, HTML2CANVAS_OPTS);
    } catch (firstErr) {
      console.warn("html2canvas on plot failed, retrying on container:", firstErr);
      if (captureTarget !== container) {
        canvas = await html2canvas(container, HTML2CANVAS_OPTS);
      } else {
        throw firstErr;
      }
    }

    if (!canvas?.width || !canvas?.height) {
      throw new Error("Captured image has zero size");
    }

    const imgData = canvas.toDataURL("image/png");
    if (!imgData || imgData.length < 100) {
      throw new Error("Captured image data is empty");
    }

    return {
      imgData,
      imgWidth: canvas.width,
      imgHeight: canvas.height,
    };
  } finally {
    await restore();
  }
}

/**
 * Raster graph + jsPDF header, download. Uses html2canvas on the visible chart.
 *
 * @param {object} opts
 * @param {import('react').RefObject<HTMLElement | null>} opts.graphRef
 * @param {import('react').MutableRefObject<{ getPlotElement?: () => HTMLElement | null | undefined } | null>} [opts.plotExportRef]
 * @param {() => { mainTitle: string; pondName: string; dateRange?: string | null; subtitle?: string | null }} opts.getGraphTitle
 * @param {(blob: Blob, filename: string, mimeType: string) => Promise<void>} opts.downloadFile
 * @param {string[]} [opts.footerNotes]
 */
export async function generateSamplingGraphPdf({
  graphRef,
  plotExportRef,
  getGraphTitle,
  downloadFile,
  footerNotes = [],
}) {
  const { imgData, imgWidth, imgHeight } = await captureGraphRaster(
    graphRef,
    plotExportRef
  );

  const pdf = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
  });

  const titleInfo = getGraphTitle();
  const graphName = titleInfo.mainTitle;
  const margin = 15;
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const black = [0, 0, 0];

  pdf.setTextColor(...black);
  let y = 14;
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(14);
  pdfTextCenteredWithWr(pdf, graphName, pageWidth, y);
  y += 10;

  pdf.setFontSize(12);
  const pondName = titleInfo.pondName;
  const pondNameWidth =
    (pdf.getStringUnitWidth(pondName) * pdf.internal.getFontSize()) /
    pdf.internal.scaleFactor;
  pdf.text(pondName, (pageWidth - pondNameWidth) / 2, y);
  y += 8;

  if (titleInfo.dateRange) {
    pdf.setFontSize(11);
    const dateWidth =
      (pdf.getStringUnitWidth(titleInfo.dateRange) * pdf.internal.getFontSize()) /
      pdf.internal.scaleFactor;
    pdf.text(titleInfo.dateRange, (pageWidth - dateWidth) / 2, y);
    y += 7;
  }

  if (titleInfo.stockedSubtitle) {
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10);
    pdfTextCenteredWithWr(pdf, titleInfo.stockedSubtitle, pageWidth, y);
    y += 7;
  }

  if (titleInfo.subtitle) {
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10);
    pdfTextCenteredWithWr(pdf, titleInfo.subtitle, pageWidth, y);
    y += 7;
  }

  if (footerNotes.length) {
    pdf.setFont("helvetica", "italic");
    pdf.setFontSize(8);
    pdf.setTextColor(55, 55, 55);
    const body = footerNotes.join(" ");
    const wrapped = pdf.splitTextToSize(body, pageWidth - 2 * margin);
    for (const line of wrapped) {
      const lineWidth =
        (pdf.getStringUnitWidth(line) * pdf.internal.getFontSize()) /
        pdf.internal.scaleFactor;
      pdf.text(line, (pageWidth - lineWidth) / 2, y);
      y += 3.6;
    }
    pdf.setTextColor(...black);
  }

  const imageY = y + 6;
  const footerReserve = 16;
  const availableWidth = pageWidth - 2 * margin;
  const availableHeight = pageHeight - imageY - margin - footerReserve;

  let renderWidth = availableWidth;
  let renderHeight = (imgHeight * renderWidth) / imgWidth;
  if (renderHeight > availableHeight) {
    renderHeight = availableHeight;
    renderWidth = (imgWidth * renderHeight) / imgHeight;
  }

  const imageX = (pageWidth - renderWidth) / 2;
  pdf.addImage(imgData, "PNG", imageX, imageY, renderWidth, renderHeight);
  drawPondStatPdfFooter(pdf, pageWidth, pageHeight, margin);

  const pdfBlob = pdf.output("blob");
  await downloadFile(pdfBlob, `${graphName}.pdf`, "application/pdf");
}
